import * as React from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Divider } from '@coursera/cds-core';
import { StyleSheet, css } from '@coursera/coursera-ui';

import CourseraPlusCollection from 'bundles/coursera-plus-landing-page/components/CourseraPlusCollection';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import CourseraPlusPartnerLogos from 'bundles/coursera-plus-landing-page/components/CourseraPlusPartnerLogos';
import CourseraPlusValueProps from 'bundles/coursera-plus-landing-page/components/CourseraPlusValueProps';
import PromoWarningModal from 'bundles/coursera-plus-landing-page/components/PromoWarningModal';
import type { RichTextObject } from 'bundles/coursera-plus-landing-page/components/SimpleRichTextContentful';
import Hero from 'bundles/coursera-plus-landing-page/components/heroContentful/Hero';
import LandingPageComparisonTableContainer from 'bundles/coursera-plus-landing-page/components/priceComparison/LandingPageComparisonTableContainer';
import StickyBannerContentful from 'bundles/coursera-plus-landing-page/components/stickyBannerContentful/StickyBannerContentful';
import {
  HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE,
  indicateRedirectedFromExpired,
  landingPageTypeChoices,
} from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import CollectionPartnerLogos from 'bundles/coursera-plus-landing-page/courseraPlusCollections/CollectionPartnerLogos';
import CollectionProductListContainer from 'bundles/coursera-plus-landing-page/courseraPlusCollections/CollectionProductListContainer';
import {
  getCollectionLandingPageFAQs,
  getCourseraLitePaidMarketingPageFAQs,
  getCourseraPlusPaidMarketingPageFAQs,
} from 'bundles/coursera-plus-landing-page/utils/getCourseraPlusFAQs';
import { newNumberOfProductsCourseraPlusNoPlusSign } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import courseraPlusExperiments from 'bundles/epic/clients/courseraPlus';
import { RegionalPromotionBanner } from 'bundles/front-page/components/banners/regional-promotion-banner/RegionalPromotionBanner';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = StyleSheet.create({
  // Note: This combination of styling fixes an accessibility issue where the PriceComparison component requires horizontal scrolling, which causes the entire page to have horizontal scrolling despite it just being extra whitespace for all other sections. This fixes it so that only the PriceComparison component has horizontal scrolling: https://coursera.atlassian.net/browse/ACCESS-2635. The PriceComparison block having horizontal scrolling should be fine for accessibility according to this StackOverflow post: https://stackoverflow.com/questions/61940426/is-horizontal-scrolling-within-a-container-considered-inaccessible-even-though/61942943#61942943
  wrapper: {
    maxWidth: '100%',
    overflowX: 'hidden',
    position: 'relative',
  },
});

// Lazy load all components that show up below the fold
const CourseraPlusSearchContainer = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/CourseraPlusSearchContainer')
);

const LearnerOutcomeSurvey = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/PeopleProfile/LearnerOutcomeSurvey')
);

const FooterContentful = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/footerContentful/FooterContentful')
);

const CourseraPlusFAQs = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/CourseraPlusFAQs')
);

const PriceComparison = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/priceComparison/PriceComparisonContentful')
);

export const sections = {
  SEARCH: 'search',
  FAQ: 'faq',
  UNIVERSITIES: 'universities',
  RECOMMENDATIONS: 'recommendations',
  STORIES: 'stories',
  PROPOSITIONS: 'propositions',
  TOP: 'top',
};

type SectionProps = {
  sectionId: string;
};

const Section: React.FC<SectionProps> = (props) => {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: '-65px' }}>
        <div id={props.sectionId} />
      </div>
    </div>
  );
};

type PropsFromRouter = {
  redirectedFromExpired?: boolean;
};

// This component starts as a copy of highlightMonthly/CourseraPlusHighlightMonthlyLandingPageVariantTwo
// (used as landing page when the Contentful version was created)
export const LandingPageContentfulContent: React.FC<PropsFromRouter> = ({ redirectedFromExpired }) => {
  const location = useLocation();
  const extendedLandingPage = useLandingPageExtended();
  const {
    isCourseraPlusOwner,
    isCourseraLiteOwner,
    userHasCourseraPlusFreeTrial,
    userHasCourseraLiteFreeTrial,
    isCourseraLiteContentfulPage,
    isCourseraLiteLandingPageEnabled,
    customProductCollectionAtTheTop,
    customProductCollectionAtBottom,
    landingPageType,
  } = extendedLandingPage;
  const {
    stickyBannerText,
    cutPartnerLogos,
    cutValueProps,
    cutRecommendations,
    cutCourseSearch,
    cutLearnerOutcome,
    cutPriceComparison,
    cutFaq,
    cutFooter,
    modalForUserWhoOwnsCourseraPlus,
    modalForUserWhoOwnsCourseraLite,
    modalForUserWhoUsedUpTheirFreeTrial,
    explanationForFaq,
    skipQuestionInFaqAboutFreeTrial,
    slug,
  } = extendedLandingPage.landingPage;
  const isComputerScienceCollectionPage = landingPageType === landingPageTypeChoices.COMPUTER_SCIENCE_COLLECTION;
  const isQ32023TentpolePromotion = ['onedollar-aug-2023', 'annual-aug-2023'].includes(slug as string);
  let topCollectionTitleOverride: string | undefined;
  let bottomCollectionTitleOverride: string | undefined;
  if (slug === 'onedollar-nov-2023') {
    topCollectionTitleOverride = _t('Launch your career');
    bottomCollectionTitleOverride = _t('Learn in-demand skills');
  }

  React.useEffect(() => {
    if (!isCourseraPlusOwner && slug === HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE && isAuthenticatedUser()) {
      // Record impression for Coursera Plus Annual Price Presentation on Landing Page (Version 2)
      // https://tools.coursera.org/epic/experiment/zyWL23rdEe-ddg5k2VLkMQ
      courseraPlusExperiments.get('courseraPlusLandingPageAnnualPricePresentationVersion2');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let faqs = getCourseraPlusPaidMarketingPageFAQs(explanationForFaq as RichTextObject, skipQuestionInFaqAboutFreeTrial);
  if (isCourseraLiteContentfulPage) {
    faqs = getCourseraLitePaidMarketingPageFAQs(explanationForFaq as RichTextObject, skipQuestionInFaqAboutFreeTrial);
  } else if (isComputerScienceCollectionPage) {
    faqs = getCollectionLandingPageFAQs(explanationForFaq as RichTextObject, skipQuestionInFaqAboutFreeTrial);
  }

  const showAllPlansComparisonTable = isCourseraLiteContentfulPage && !isCourseraPlusOwner && !isCourseraLiteOwner;
  let modalMessage;
  let redireactUrlIfNotEligible;
  const hasFreeTrial = isCourseraLiteContentfulPage ? userHasCourseraLiteFreeTrial : userHasCourseraPlusFreeTrial;
  const isSelfReferralRedirect = location.query.referralRedirect === 'selfReferral';
  if (!hasFreeTrial && modalForUserWhoUsedUpTheirFreeTrial) {
    modalMessage = modalForUserWhoUsedUpTheirFreeTrial;
  }
  if (isCourseraPlusOwner && modalForUserWhoOwnsCourseraPlus) {
    modalMessage = modalForUserWhoOwnsCourseraPlus;
    redireactUrlIfNotEligible = '/courseraplus';
  }
  if (isCourseraLiteContentfulPage && isCourseraLiteOwner && modalForUserWhoOwnsCourseraLite) {
    modalMessage = modalForUserWhoOwnsCourseraLite;
    redireactUrlIfNotEligible = '/courseraplus';
  }
  if (redirectedFromExpired) {
    // this is when we view the C+ home instead of the expired page
    modalMessage = _t('Sorry, the offer you tried to view is no longer available');
  }

  if (isSelfReferralRedirect) {
    modalMessage = _t('Sorry, you are not eligible for this promotion.');
  }

  if (isCourseraLiteContentfulPage && isAuthenticatedUser() && !isCourseraLiteLandingPageEnabled) {
    modalMessage = _t('Sorry, you are not eligible for this offer at this time.');
    redireactUrlIfNotEligible = '/courseraplus';
  }

  const getPromotionalBanner = () => {
    if (extendedLandingPage.hideRegionalPromotionBanner) {
      return null;
    }

    return <RegionalPromotionBanner variant="blue" />;
  };

  return (
    <div {...css(styles.wrapper)}>
      {getPromotionalBanner()}
      {modalMessage && (
        <PromoWarningModal message={modalMessage} redirectUrlIfNotEligible={redireactUrlIfNotEligible} />
      )}
      <StickyBannerContentful bannerText={stickyBannerText} isCourseraLiteContentfulPage={isCourseraLiteContentfulPage}>
        <Hero isCourseraLiteContentfulPage={isCourseraLiteContentfulPage} />
      </StickyBannerContentful>
      <Section sectionId={sections.UNIVERSITIES} />
      {!cutPartnerLogos && <CourseraPlusPartnerLogos isCourseraLiteContentfulPage={isCourseraLiteContentfulPage} />}
      {isComputerScienceCollectionPage && (
        <>
          <CollectionPartnerLogos />
          <CollectionProductListContainer
            slug={slug}
            ownsCourseraPlus={isCourseraPlusOwner}
            ownsCourseraLite={isCourseraLiteOwner}
          />
        </>
      )}
      <Section sectionId={sections.PROPOSITIONS} />
      <Divider />
      {!cutValueProps && <CourseraPlusValueProps isCourseraLiteContentfulPage={isCourseraLiteContentfulPage} />}
      <Section sectionId={sections.RECOMMENDATIONS} />
      {!cutRecommendations && (
        <CourseraPlusCollection
          isCourseraLiteContentfulPage={isCourseraLiteContentfulPage}
          additionalCollectionItemProps={{ isInteractive: false }}
          collectionSlug={customProductCollectionAtTheTop}
          titleOverride={topCollectionTitleOverride}
          isDefaultLandingPage={slug === HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE}
        />
      )}
      <Section sectionId={sections.SEARCH} />
      {!cutCourseSearch && (
        <CourseraPlusSearchContainer
          isCourseraLiteContentfulPage={isCourseraLiteContentfulPage}
          additionalSearchResultsProps={{ removeLinks: true }}
          ownsCourseraPlus={isCourseraPlusOwner}
          numberOfProductsInPlusOverride={
            isQ32023TentpolePromotion ? newNumberOfProductsCourseraPlusNoPlusSign : undefined
          }
        />
      )}
      <Divider />
      <Section sectionId={sections.STORIES} />
      {!cutLearnerOutcome && !isCourseraLiteContentfulPage && <LearnerOutcomeSurvey />}
      {!cutPriceComparison && !isCourseraLiteContentfulPage && <PriceComparison landingPage={extendedLandingPage} />}
      {showAllPlansComparisonTable && (
        <LandingPageComparisonTableContainer
          userHasCourseraLiteFreeTrial={userHasCourseraLiteFreeTrial}
          userHasCourseraPlusFreeTrial={userHasCourseraPlusFreeTrial}
          isCourseraLiteLandingPageEnabled={isCourseraLiteLandingPageEnabled}
        />
      )}
      {!cutRecommendations && customProductCollectionAtBottom && (
        <CourseraPlusCollection
          isCourseraLiteContentfulPage={isCourseraLiteContentfulPage}
          additionalCollectionItemProps={{ isInteractive: false }}
          collectionSlug={customProductCollectionAtBottom}
          titleOverride={bottomCollectionTitleOverride}
        />
      )}
      <Section sectionId={sections.FAQ} />
      {!cutFaq && <CourseraPlusFAQs faqs={faqs} />}
      {!cutFooter && <FooterContentful isCourseraLiteContentfulPage={isCourseraLiteContentfulPage} />}
    </div>
  );
};

const LandingPageContentfulContentContainer = () => {
  const location = useLocation();
  const redirectedFromExpired = location.search.indexOf(indicateRedirectedFromExpired) >= 0;
  return <LandingPageContentfulContent redirectedFromExpired={redirectedFromExpired} />;
};

export default LandingPageContentfulContentContainer;
